import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/Brand";
import TaskCard from "@/components/TaskCard";
import AdminStudents from "@/pages/AdminStudents";
import { AdminActivity } from "@/pages/Activity";
import { api, errMsg, useData } from "@/lib/api";

const TEMPLATE_LABELS = { data_entry: "Data Entry — New Lead", data_management: "Data Management — Contact Records", calendar: "Calendar — Discovery Call", email_drip: "Email — AVY Clients #1" };

function TasksTab({ studentId, setStudentId }) {
  const [students] = useData("/admin/students", []);
  const [tasks, reload] = useData(studentId ? `/admin/tasks?student_id=${studentId}` : null, []);
  const [custom, setCustom] = useState({ title: "", body: "" });
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    if (!studentId && students.length) setStudentId(students[0].id);
  }, [students, studentId, setStudentId]);

  const assign = async (body) => {
    try {
      await api.post("/admin/tasks", { student_id: studentId, ...body });
      toast.success("Task assigned");
      setCustom({ title: "", body: "" });
      setShowCustom(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const missing = Object.keys(TEMPLATE_LABELS).filter((k) => !tasks.some((t) => t.key === k));
  const completed = tasks.filter((t) => t.status === "Completed").length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={studentId || ""} onValueChange={setStudentId}>
          <SelectTrigger className="w-72" data-testid="tasks-student-select"><SelectValue placeholder="Select an associate" /></SelectTrigger>
          <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`tasks-student-option-${s.id}`}>{s.name} — {s.status}</SelectItem>)}</SelectContent>
        </Select>
        {studentId && <span className="text-sm text-stone-500" data-testid="tasks-summary">{completed}/{tasks.length} completed</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          {missing.map((k) => <Button key={k} size="sm" variant="outline" className="border-[#D4AF37]" onClick={() => assign({ key: k })} data-testid={`assign-task-${k}`}><Plus className="h-3.5 w-3.5 mr-1" />{TEMPLATE_LABELS[k]}</Button>)}
          <Button size="sm" variant="outline" onClick={() => setShowCustom((v) => !v)} data-testid="toggle-custom-task-btn"><Plus className="h-3.5 w-3.5 mr-1" />Custom request</Button>
        </div>
      </div>
      {showCustom && (
        <div className="rounded-2xl border bg-[#FDFDFE] p-4 space-y-3" data-testid="custom-task-form">
          <Input placeholder="Request title (e.g. Follow up with Marcus Reed)" value={custom.title} onChange={(e) => setCustom({ ...custom, title: e.target.value })} data-testid="custom-task-title-input" />
          <Textarea rows={3} placeholder="Message the associate will see in their Requests inbox" value={custom.body} onChange={(e) => setCustom({ ...custom, body: e.target.value })} data-testid="custom-task-body-input" />
          <Button size="sm" className="bg-[#3B185F]" onClick={() => assign(custom)} data-testid="custom-task-save-btn">Assign request</Button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {tasks.map((t) => <TaskCard key={t.id} t={t} onChanged={reload} />)}
      </div>
      {studentId && !tasks.length && <div className="rounded-2xl border border-dashed p-12 text-center text-stone-500" data-testid="tasks-empty">No tasks assigned yet.</div>}
    </div>
  );
}

function SubmissionsTab() {
  const [items, reload] = useData("/admin/submissions", []);
  const [filter, setFilter] = useState("Submitted");
  const shown = items.filter((t) => filter === "all" || t.status === filter);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {[["Submitted", "Awaiting review"], ["Needs Correction", "Needs correction"], ["Completed", "Completed"], ["all", "All"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setFilter(v)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === v ? "bg-[#3B185F] text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`submissions-filter-${v.replace(" ", "-")}`}>
            {l} <span className="opacity-60">{items.filter((t) => v === "all" || t.status === v).length}</span>
          </button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">{shown.map((t) => <TaskCard key={t.id} t={t} onChanged={reload} showStudent />)}</div>
      {!shown.length && <div className="rounded-2xl border border-dashed p-12 text-center text-stone-500" data-testid="submissions-empty">Nothing here yet.</div>}
    </div>
  );
}

export default function AdminTraining() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "tasks";
  const [studentId, setStudentId] = useState(params.get("user") || "");
  const go = (t, user) => setParams(user ? { tab: t, user } : { tab: t });

  return (
    <div>
      <PageHeader title="Training Panel" subtitle="Assign practical tasks, review submitted work, follow activity and manage AVY Associates." />
      <Tabs value={tab} onValueChange={(t) => go(t)}>
        <TabsList className="mb-6">
          {[["tasks", "Tasks"], ["submissions", "Submissions"], ["activity", "Activity"], ["users", "User Management"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`training-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="tasks"><TasksTab studentId={studentId} setStudentId={setStudentId} /></TabsContent>
        <TabsContent value="submissions"><SubmissionsTab /></TabsContent>
        <TabsContent value="activity"><AdminActivity key={params.get("user") || "all"} initialUser={params.get("user")} /></TabsContent>
        <TabsContent value="users"><AdminStudents onViewActivity={(id) => go("activity", id)} onViewTasks={(id) => { setStudentId(id); go("tasks", id); }} /></TabsContent>
      </Tabs>
    </div>
  );
}
